/**
 * The marketing navigation hint, from the dashboard's side.
 *
 * `urlshot_session_hint=1` is set by the Platform API on the parent domain so the static marketing
 * site can render `Dashboard` instead of `Login`. It carries no token, no identity, and no plan --
 * only the single character `1` -- and no server ever reads it.
 *
 * The dashboard's only responsibility is cleanup. The specification requires the hint to be
 * cleared when the dashboard discovers its real session has expired or been revoked, because the
 * server cannot know that: nobody asked it. Without this, a customer whose session expired would
 * keep seeing `Dashboard` on the marketing site and keep bouncing to the login screen.
 *
 * Clearing is best effort. Failing to remove a presentation hint must never break the page.
 */
const HINT_COOKIE_NAME = 'urlshot_session_hint';

/**
 * Candidate domains to clear the cookie on.
 *
 * A cookie can only be removed by an expiry that repeats its original `Domain` attribute, and the
 * dashboard does not know which one the server used. `app.urlshot.io` was given `Domain=urlshot.io`,
 * so both the host-only form and the parent-domain form are attempted.
 */
function clearanceDomains(): (string | null)[] {
  const { hostname } = window.location;
  const parts = hostname.split('.');
  if (parts.length < 2) return [null];
  return [null, `.${parts.slice(-2).join('.')}`];
}

/** Expire the marketing hint. Safe to call when it is already absent. */
export function clearSessionHint(): void {
  try {
    for (const domain of clearanceDomains()) {
      const attributes = [
        `${HINT_COOKIE_NAME}=`,
        'Path=/',
        'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
        'SameSite=Lax',
        ...(domain === null ? [] : [`Domain=${domain}`]),
      ];
      document.cookie = attributes.join('; ');
    }
  } catch {
    // Presentation only; never worth surfacing.
  }
}
